import React from 'react';
import PropTypes from 'prop-types';

const OnboardingHeaderContext = React.createContext(undefined);

// Actions
export const SET_HEADER_THEME_LIGHT = 'set-header-theme-light';
export const SET_HEADER_THEME_DARK = 'set-header-theme-dark';
export const NAV_ICON_HIGHLIGHT_ON = 'nav-icon-highlight-on';
export const NAV_ICON_HIGHLIGHT_OFF = 'nav-icon-highlight-off';
export const CHAT_ICON_HIGHLIGHT_ON = 'chat-icon-highlight-on';
export const CHAT_ICON_HIGHLIGHT_OFF = 'chat-icon-highlight-off';

const HEADER_THEME_MAP = {
  light: 'light',
  dark: 'dark',
};

const init = () => {
  return {
    headerTheme: HEADER_THEME_MAP.light,
    navIconHighlight: false,
    chatIconHighlight: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SET_HEADER_THEME_LIGHT: {
      return {
        ...state,
        headerTheme: HEADER_THEME_MAP.light,
      };
    }
    case SET_HEADER_THEME_DARK: {
      return {
        ...state,
        headerTheme: HEADER_THEME_MAP.dark,
      };
    }
    case NAV_ICON_HIGHLIGHT_ON: {
      return {
        ...state,
        navIconHighlight: true,
      };
    }
    case NAV_ICON_HIGHLIGHT_OFF: {
      return {
        ...state,
        navIconHighlight: false,
      };
    }
    case CHAT_ICON_HIGHLIGHT_ON: {
      return {
        ...state,
        chatIconHighlight: true,
      };
    }
    case CHAT_ICON_HIGHLIGHT_OFF: {
      return {
        ...state,
        chatIconHighlight: false,
      };
    }
    default: {
      throw new Error(`Unhandled SurveyContext action type: ${action.type}`);
    }
  }
};

const OnboardingHeaderProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handleSetHeaderThemeLight = React.useCallback(() => {
    dispatch({ type: SET_HEADER_THEME_LIGHT });
  }, []);

  const handleSetHeaderThemeDark = React.useCallback(() => {
    dispatch({ type: SET_HEADER_THEME_DARK });
  }, []);

  const handleSetNavIconHighlight = React.useCallback((on = false) => {
    if (on) {
      dispatch({ type: NAV_ICON_HIGHLIGHT_ON });
    } else {
      dispatch({ type: NAV_ICON_HIGHLIGHT_OFF });
    }
  }, []);

  const handleSetChatIconHighlight = React.useCallback((on = false) => {
    if (on) {
      dispatch({ type: CHAT_ICON_HIGHLIGHT_ON });
    } else {
      dispatch({ type: CHAT_ICON_HIGHLIGHT_OFF });
    }
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      isHeaderThemeDark: state.headerTheme === HEADER_THEME_MAP.dark,
      handleSetHeaderThemeLight,
      handleSetHeaderThemeDark,
      navIconHighlighted: state.navIconHighlight,
      handleSetNavIconHighlight,
      chatIconHighlighted: state.chatIconHighlight,
      handleSetChatIconHighlight,
    };
  }, [
    state,
    handleSetHeaderThemeLight,
    handleSetHeaderThemeDark,
  ]);

  return (
    <OnboardingHeaderContext.Provider value={memoizedValue}>
      {children}
    </OnboardingHeaderContext.Provider>
  );
};

// Use Survey State Context
const useOnboardingHeaderContext = () => {
  const context = React.useContext(OnboardingHeaderContext);
  if (context === undefined) {
    throw new Error('useOnboardingHeaderContext must be used within a OnboardingHeaderProvider');
  }
  return context;
};

OnboardingHeaderProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  OnboardingHeaderProvider,
  useOnboardingHeaderContext,
};
